import { FC, useState, useMemo } from "react"
import { Brand, ConnectorFormValues } from "../../../../../../types"
import { FormikErrors } from "formik"
import { useFetchLoanOfficersQuery } from "../../../../../../redux/queries"
import { useTemplateLoader } from "../hooks/useTemplateLoader"
import { useConnectorFormState } from "../hooks/useConnectorFormState"
import BBSwitch from "../../../../form/Switch"
import FieldMapper from "../field-mapper"
import BasicInfoSection from "./sections/BasicInfoSection"
import WebhookUrlSection from "./sections/WebhookUrlSection"
import ConfigurationSection from "./sections/ConfigurationSection"
import AdvancedSettingsSection from "./sections/AdvancedSettingsSection"

interface Props {
  values: ConnectorFormValues
  brands: Brand[]
  setFormField: (name: string, val: any) => void
  isCreate: boolean
  errors: FormikErrors<ConnectorFormValues>
  validateForm?: () => void
  setFieldTouched?: (field: string, touched: boolean) => void
}

const ManageConnectorFormContent: FC<Props> = ({
  values,
  brands,
  setFormField,
  isCreate,
  errors,
  validateForm,
  setFieldTouched
}) => {
  const [loQuery, setLoQuery] = useState("")

  // Fetch loan officers for the selected brand
  const { data: loanOfficersData } = useFetchLoanOfficersQuery(
    { brand_id: values.brand_id, limit: 100 },
    { skip: !values.brand_id }
  )

  // Loan officer options with search filtering
  const loOptions = useMemo(() => {
    if (!loanOfficersData?.los) return []

    return loanOfficersData.los
      .filter((lo: any) => {
        const searchLower = loQuery.toLowerCase()
        return (
          lo.full_name?.toLowerCase().includes(searchLower) ||
          lo.name?.toLowerCase().includes(searchLower) ||
          lo.uid?.toString().includes(loQuery) ||
          lo.id?.toString().includes(loQuery)
        )
      })
      .map((lo: any) => {
        const name = lo.full_name || lo.name || "Unnamed"
        const id = lo.uid || lo.id
        return {
          id: id?.toString(),
          text: `${name} (${id})`
        }
      })
  }, [loanOfficersData, loQuery])

  const selectedLo = loOptions.find(opt => opt.id === values.owner)

  // Custom hooks for business logic
  const { availableTemplates, loadTemplate } = useTemplateLoader({
    dataType: values.data_type,
    direction: values.direction,
    setFormField,
    currentConfig: values.config
  })

  const {
    handleImport,
    handleExport,
    handleDataTypeChange,
    handleContentTypeChange,
    handleHeadersChange,
    handleTimeoutChange,
    handleBasicAuthChange,
    copyWebhookUrl
  } = useConnectorFormState({
    values,
    setFormField,
    setFieldTouched,
    validateForm
  })

  const handleCopyWebhookUrl = () => {
    const success = copyWebhookUrl()
    if (success) {
      alert("Webhook URL copied to clipboard!")
    }
  }

  return (
    <div className="space-y-6 pt-4">
      {!isCreate && (
        <div className="border-b pb-4">
          <BBSwitch
            enabled={Number(values.active) === 1}
            setEnabled={val => setFormField("active", val ? 1 : 0)}
            label="Active"
            description={
              Number(values.active) === 1
                ? "This connector is enabled and will fire on events"
                : "This connector is disabled and will not fire"
            }
          />
        </div>
      )}

      <BasicInfoSection
        values={values}
        brands={brands}
        setFormField={setFormField}
        isCreate={isCreate}
        errors={errors}
        loOptions={loOptions}
        selectedLo={selectedLo}
        loQuery={loQuery}
        setLoQuery={setLoQuery}
        onImport={handleImport}
        onExport={handleExport}
      />

      {/* Webhook URL Section - Prominent display for incoming connectors */}
      {values.direction === "incoming" &&
        !isCreate &&
        values.incoming_webhook_url && (
          <WebhookUrlSection
            values={values}
            onCopyWebhookUrl={handleCopyWebhookUrl}
          />
        )}

      <ConfigurationSection
        values={values}
        setFormField={setFormField}
        isCreate={isCreate}
        errors={errors}
        onContentTypeChange={handleContentTypeChange}
        onDataTypeChange={handleDataTypeChange}
        availableTemplates={availableTemplates}
        onTemplateLoad={loadTemplate}
      />

      {/* Field Mapping Section — hidden for lead_score and pending_subscription (canonical schema, no mapping needed) */}
      {values.data_type !== "lead_score" &&
        !(
          values.data_type === "pending_subscription" &&
          values.direction === "outgoing"
        ) && (
          <div className="border-b pb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Field Mapping</h3>
              {availableTemplates.length > 0 && (
                <select
                  onChange={e => loadTemplate(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                  defaultValue=""
                >
                  <option value="">Load Sample...</option>
                  {availableTemplates.map(template => (
                    <option key={template.name} value={template.name}>
                      {template.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {values.direction === "incoming"
                ? "Map fields from your CRM to Banking Bridge. CRM field paths support dot notation (e.g., homebuyer.email, loan.loan-purpose)."
                : "Map fields from Banking Bridge to your CRM. The left column is the Banking Bridge field, and the right column is the CRM field path (supports dot notation like name.first)."}
            </p>

            <FieldMapper
              fieldMapping={values.field_mapping}
              dataType={values.data_type}
              direction={values.direction}
              onChange={mapping => setFormField("field_mapping", mapping)}
            />
          </div>
        )}

      <AdvancedSettingsSection
        values={values}
        onHeadersChange={handleHeadersChange}
        onTimeoutChange={handleTimeoutChange}
        onBasicAuthChange={handleBasicAuthChange}
      />
    </div>
  )
}

export default ManageConnectorFormContent
